import init, {
  type Diagnostic,
  PositionEncoding,
  Workspace,
} from "@astral-sh/ruff-wasm-web";

import type { CodeLanguage } from "./code-language";
import { formatSql } from "./sql-format";
import {
  analyzeSql,
  formatErrorLines,
  isReadOnly,
  type CodeFormatErrorRange,
} from "./sql-validation";

export type { CodeFormatErrorRange };
export { inspectSqlFormatErrors } from "./sql-validation";

export class CodeFormatError extends Error {
  constructor(
    message: string,
    readonly lines: readonly number[],
    readonly ranges: readonly CodeFormatErrorRange[] = [],
  ) {
    super(message);
    this.name = "CodeFormatError";
  }
  get line(): number | null {
    return this.lines[0] ?? null;
  }
}

export function getFormatErrorLine(error: unknown): number | null {
  return error instanceof CodeFormatError ? error.line : null;
}
export function getFormatErrorLines(error: unknown): readonly number[] {
  return error instanceof CodeFormatError ? error.lines : [];
}
export function getFormatErrorRanges(
  error: unknown,
): readonly CodeFormatErrorRange[] {
  return error instanceof CodeFormatError ? error.ranges : [];
}

function validateSqlQuery(source: string): void {
  const { statements, errors } = analyzeSql(source);
  if (errors.length > 0) {
    throw new CodeFormatError(
      "Invalid T-SQL query",
      formatErrorLines(source, errors),
      errors,
    );
  }

  const statement = statements[0];
  if (statement && !isReadOnly(statement)) {
    const writeLine = formatErrorLines(source, [
      { from: statement.start, to: statement.end },
    ])[0];
    throw new CodeFormatError(
      "Workflow SQL must be a read-only query",
      writeLine === undefined ? [] : [writeLine],
    );
  }
}

function diagnosticLines(diagnostics: Diagnostic[]): number[] {
  return [...new Set(diagnostics.map((item) => item.start_location.row))]
    .filter((line) => line > 0)
    .sort((left, right) => left - right);
}

export async function formatCode(
  language: CodeLanguage,
  source: string,
): Promise<string> {
  if (source === "") {
    return source;
  }
  if (language === "sql") {
    validateSqlQuery(source);
    return formatSql(source);
  }

  await init();
  const workspace = new Workspace(
    Workspace.defaultSettings(),
    PositionEncoding.Utf16,
  );
  try {
    return workspace.format(source);
  } catch (error) {
    throw new CodeFormatError(
      error instanceof Error ? error.message : "Formatting failed",
      diagnosticLines(workspace.check(source) as Diagnostic[]),
    );
  }
}

/** Cosmetic formatting for read-only display; skips workflow SQL validation. */
export async function formatCodeForDisplay(
  language: CodeLanguage,
  source: string,
): Promise<string> {
  if (source === "") {
    return source;
  }
  if (language === "sql") {
    return formatSql(source);
  }
  return formatCode(language, source);
}
